import { useEffect, useRef } from 'react';
import { isAxiosError } from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import { useClinicContext } from './useClinicContext';
import useAppToast from '../useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import {
  FreeMedicalTreatmentCategory,
  FreeMedicalTreatmentType,
  CategoryFormData,
  TreatmentTypeFormData,
  CategoryListData,
  TreatmentTypeListData,
  FreeMedicalInitData,
} from '@/pages/main/freeMedical/types';

// ==================== API Service Functions ====================

/**
 * API Response interface
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: Record<string, string>;
}

/**
 * Get service type header value
 */
const getServiceTypeHeader = (): string => {
  return localStorage.getItem('service_type') || SERVICE_TYPE_A2;
};

/**
 * Get initialization data (product sale types, categories)
 */
const getInitData = async (
  siteType: string,
  clinicNumber: string
): Promise<ApiResponse<FreeMedicalInitData>> => {
  const response = await apiClient.get<ApiResponse<FreeMedicalInitData>>(
    clinicApiPath(siteType, clinicNumber, '/free-medical/init'),
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

// ==================== Category Methods ====================

/**
 * Get paginated list of categories
 */
const getCategories = async (
  siteType: string,
  clinicNumber: string,
  params?: { per_page?: number; page?: number; sort_field?: string; sort_order?: string }
): Promise<ApiResponse<CategoryListData>> => {
  const response = await apiClient.get<ApiResponse<CategoryListData>>(
    clinicApiPath(siteType, clinicNumber, '/free-medical/categories'),
    {
      params,
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Get a single category by ID
 */
export const getCategoryById = async (
  siteType: string,
  clinicNumber: string,
  id: number
): Promise<ApiResponse<FreeMedicalTreatmentCategory>> => {
  const response = await apiClient.get<ApiResponse<FreeMedicalTreatmentCategory>>(
    clinicApiPath(siteType, clinicNumber, `/free-medical/categories/${id}`),
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Create a new category
 */
const createCategory = async (
  siteType: string,
  clinicNumber: string,
  data: CategoryFormData
): Promise<ApiResponse<FreeMedicalTreatmentCategory>> => {
  const response = await apiClient.post<ApiResponse<FreeMedicalTreatmentCategory>>(
    clinicApiPath(siteType, clinicNumber, '/free-medical/categories'),
    data,
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Update an existing category
 */
const updateCategory = async (
  siteType: string,
  clinicNumber: string,
  id: number,
  data: Partial<CategoryFormData>
): Promise<ApiResponse<FreeMedicalTreatmentCategory>> => {
  const response = await apiClient.put<ApiResponse<FreeMedicalTreatmentCategory>>(
    clinicApiPath(siteType, clinicNumber, `/free-medical/categories/${id}`),
    data,
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Delete a category
 */
const deleteCategory = async (
  siteType: string,
  clinicNumber: string,
  id: number
): Promise<ApiResponse<null>> => {
  const response = await apiClient.delete<ApiResponse<null>>(
    clinicApiPath(siteType, clinicNumber, `/free-medical/categories/${id}`),
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Reorder categories
 */
const reorderCategories = async (
  siteType: string,
  clinicNumber: string,
  sortOrder: Record<number, number>
): Promise<ApiResponse<null>> => {
  const response = await apiClient.post<ApiResponse<null>>(
    clinicApiPath(siteType, clinicNumber, '/free-medical/categories/reorder'),
    { sort_order: sortOrder },
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

// ==================== Treatment Type Methods ====================

/**
 * Get paginated list of treatment types
 */
const getTreatmentTypes = async (
  siteType: string,
  clinicNumber: string,
  params?: {
    per_page?: number;
    page?: number;
    category_id?: number;
    sort_field?: string;
    sort_order?: string;
  }
): Promise<ApiResponse<TreatmentTypeListData>> => {
  const response = await apiClient.get<ApiResponse<TreatmentTypeListData>>(
    clinicApiPath(siteType, clinicNumber, '/free-medical/treatment-types'),
    {
      params,
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Get a single treatment type by ID
 */
export const getTreatmentTypeById = async (
  siteType: string,
  clinicNumber: string,
  id: number
): Promise<ApiResponse<FreeMedicalTreatmentType>> => {
  const response = await apiClient.get<ApiResponse<FreeMedicalTreatmentType>>(
    clinicApiPath(siteType, clinicNumber, `/free-medical/treatment-types/${id}`),
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Create a new treatment type
 */
const createTreatmentType = async (
  siteType: string,
  clinicNumber: string,
  data: TreatmentTypeFormData
): Promise<ApiResponse<FreeMedicalTreatmentType>> => {
  const response = await apiClient.post<ApiResponse<FreeMedicalTreatmentType>>(
    clinicApiPath(siteType, clinicNumber, '/free-medical/treatment-types'),
    data,
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Update an existing treatment type
 */
const updateTreatmentType = async (
  siteType: string,
  clinicNumber: string,
  id: number,
  data: Partial<TreatmentTypeFormData>
): Promise<ApiResponse<FreeMedicalTreatmentType>> => {
  const response = await apiClient.put<ApiResponse<FreeMedicalTreatmentType>>(
    clinicApiPath(siteType, clinicNumber, `/free-medical/treatment-types/${id}`),
    data,
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Delete a treatment type
 */
const deleteTreatmentType = async (
  siteType: string,
  clinicNumber: string,
  id: number
): Promise<ApiResponse<null>> => {
  const response = await apiClient.delete<ApiResponse<null>>(
    clinicApiPath(siteType, clinicNumber, `/free-medical/treatment-types/${id}`),
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Reorder treatment types
 */
const reorderTreatmentTypes = async (
  siteType: string,
  clinicNumber: string,
  sortOrder: Record<number, number>
): Promise<ApiResponse<null>> => {
  const response = await apiClient.post<ApiResponse<null>>(
    clinicApiPath(siteType, clinicNumber, '/free-medical/treatment-types/reorder'),
    { sort_order: sortOrder },
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

// ==================== Hooks ====================

/**
 * Hook to get initialization data
 */
export const useMainFreeMedicalInit = () => {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const query = useQuery({
    queryKey: ['free-medical', 'init', tenant, clinicNumber],
    queryFn: () => getInitData(tenant, clinicNumber),
    enabled: !!tenant && !!clinicNumber,
  });

  const { isError, error } = query;
  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error as Error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return query;
};

// ==================== Category Hooks ====================

/**
 * Hook to get paginated list of categories
 */
export const useMainFreeMedicalCategories = (params?: {
  per_page?: number;
  page?: number;
  sort_field?: string;
  sort_order?: string;
}) => {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const query = useQuery({
    queryKey: ['free-medical', 'categories', tenant, clinicNumber, params],
    queryFn: () => getCategories(tenant, clinicNumber, params),
    enabled: !!tenant && !!clinicNumber,
  });

  const { isError, error } = query;
  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error as Error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error?.message, toast]);

  return query;
};

/**
 * Hook to get a single category by ID
 */
export const useMainFreeMedicalCategory = (id: number) => {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const query = useQuery({
    queryKey: ['free-medical', 'categories', tenant, clinicNumber, id],
    queryFn: () => getCategoryById(tenant, clinicNumber, id),
    enabled: !!tenant && !!clinicNumber && !!id,
  });

  const { isError, error } = query;
  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error as Error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return query;
};

/**
 * Hook to create a category
 */
export const useMainFreeMedicalCategoryCreate = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: CategoryFormData) => createCategory(tenant, clinicNumber, data),
    onSuccess: response => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['free-medical', 'categories'] });
        queryClient.invalidateQueries({ queryKey: ['free-medical', 'init'] });
        toast.success(response.message ?? 'カテゴリを更新しました。');
      } else {
        toast.error(response.message ?? '登録に失敗しました。');
      }
    },
    onError: error => {
      toast.error(getApiErrorMessage(error as Error, '登録に失敗しました。'));
    },
  });

  return { mutateAsync, isLoading: isPending };
};

/**
 * Hook to update a category
 */
export const useMainFreeMedicalCategoryUpdate = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CategoryFormData> }) =>
      updateCategory(tenant, clinicNumber, id, data),
    onSuccess: response => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['free-medical', 'categories'] });
        queryClient.invalidateQueries({ queryKey: ['free-medical', 'init'] });
        queryClient.invalidateQueries({
          queryKey: ['free-medical', 'categories', response.data?.id],
        });
        // The 自由診療 tab shows the category name, so it must not keep the old one
        queryClient.invalidateQueries({ queryKey: ['free-medical', 'treatment-types'] });
        toast.success(response.message ?? 'カテゴリを更新しました。');
      } else {
        toast.error(response.message ?? '更新に失敗しました。');
      }
    },
    onError: error => {
      toast.error(getApiErrorMessage(error as Error, '更新に失敗しました。'));
    },
  });

  return { mutateAsync, isLoading: isPending };
};

/** Message when category cannot be deleted because it has treatment types (自由診療メニュー) */
const CATEGORY_IN_USE_DELETE_MESSAGE =
  'このカテゴリには自由診療が登録されているため削除できません。';

/**
 * Hook to delete a category
 */
export const useMainFreeMedicalCategoryDelete = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: number) => deleteCategory(tenant, clinicNumber, id),
    onSuccess: response => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['free-medical', 'categories'] });
        queryClient.invalidateQueries({ queryKey: ['free-medical', 'init'] });
        toast.success(response.message ?? 'カテゴリを削除しました。');
      } else {
        // When deletion fails (success=false), use message from response or default
        const errorMessage = response.message || CATEGORY_IN_USE_DELETE_MESSAGE;
        toast.error(errorMessage);
      }
    },
    onError: error => {
      const err = error as Error & { response?: { status?: number; data?: { message?: string } } };
      const isValidation422 = err.response?.status === 422;
      const message =
        isValidation422 && err.response?.data?.message
          ? err.response.data.message
          : isValidation422
            ? CATEGORY_IN_USE_DELETE_MESSAGE
            : getApiErrorMessage(error as Error, '削除に失敗しました。');
      toast.error(message);
      queryClient.invalidateQueries({ queryKey: ['free-medical', 'categories'] });
    },
  });

  return { mutateAsync, isLoading: isPending };
};

/**
 * Hook to reorder categories
 */
export const useMainFreeMedicalCategoryReorder = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (sortOrder: Record<number, number>) =>
      reorderCategories(tenant, clinicNumber, sortOrder),
    // optimistic update
    onMutate: async sortOrder => {
      await queryClient.cancelQueries({ queryKey: ['free-medical', 'categories'] });

      const previousQueries = queryClient.getQueriesData<ApiResponse<CategoryListData>>({
        queryKey: ['free-medical', 'categories'],
      });

      previousQueries.forEach(([key, data]) => {
        if (data?.data?.data) {
          const items = data.data.data;
          const reordered = [...items].sort(
            (a, b) => (sortOrder[a.id] ?? 0) - (sortOrder[b.id] ?? 0)
          );
          queryClient.setQueryData(key, {
            ...data,
            data: { ...data.data, data: reordered },
          });
        }
      });

      return { previousQueries };
    },
    onError: (error, _vars, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([key, data]) => {
          queryClient.setQueryData(key, data);
        });
      }
      toast.error(getApiErrorMessage(error as Error, '並び順の更新に失敗しました。'));
    },
    onSuccess: response => {
      if (response.success) {
        toast.success(response.message ?? '並び順を更新しました。');
        // also refresh init payload so dropdown order stays in sync
        queryClient.invalidateQueries({ queryKey: ['free-medical', 'init'] });
      } else {
        toast.error(response.message ?? '並び順の更新に失敗しました。');
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['free-medical', 'categories'] });
      queryClient.invalidateQueries({ queryKey: ['free-medical', 'init'] });
    },
  });

  return { mutateAsync, isLoading: isPending };
};

/**
 * Hook to reorder treatment types
 */
export const useMainFreeMedicalTreatmentTypeReorder = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (sortOrder: Record<number, number>) =>
      reorderTreatmentTypes(tenant, clinicNumber, sortOrder),
    onMutate: async sortOrder => {
      await queryClient.cancelQueries({ queryKey: ['free-medical', 'treatment-types'] });

      const previousQueries = queryClient.getQueriesData<ApiResponse<TreatmentTypeListData>>({
        queryKey: ['free-medical', 'treatment-types'],
      });

      previousQueries.forEach(([key, data]) => {
        if (data?.data?.data) {
          const items = data.data.data;
          const reordered = [...items].sort(
            (a, b) => (sortOrder[a.id] ?? 0) - (sortOrder[b.id] ?? 0)
          );
          queryClient.setQueryData(key, {
            ...data,
            data: { ...data.data, data: reordered },
          });
        }
      });

      return { previousQueries };
    },
    onError: (error, _vars, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([key, data]) => {
          queryClient.setQueryData(key, data);
        });
      }
      toast.error(getApiErrorMessage(error as Error, '並び順の更新に失敗しました。'));
    },
    onSuccess: response => {
      if (response.success) {
        toast.success(response.message ?? '並び順を更新しました。');
        queryClient.invalidateQueries({ queryKey: ['free-medical', 'init'] });
      } else {
        toast.error(response.message ?? '並び順の更新に失敗しました。');
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['free-medical', 'treatment-types'] });
      queryClient.invalidateQueries({ queryKey: ['free-medical', 'init'] });
    },
  });

  return { mutateAsync, isLoading: isPending };
};

// ==================== Treatment Type Hooks ====================

/**
 * Hook to get paginated list of treatment types
 */
export const useMainFreeMedicalTreatmentTypes = (params?: {
  per_page?: number;
  page?: number;
  category_id?: number;
  sort_field?: string;
  sort_order?: string;
}) => {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();
  const lastToastKeyRef = useRef<string | null>(null);

  const query = useQuery({
    queryKey: ['free-medical', 'treatment-types', tenant, clinicNumber, params],
    queryFn: () => getTreatmentTypes(tenant, clinicNumber, params),
    enabled: !!tenant && !!clinicNumber,
    retry: (failureCount, error) => {
      if (isAxiosError(error) && error.response?.status === 422) return false;
      return failureCount < 2;
    },
  });

  const { isError, error } = query;
  useEffect(() => {
    if (isError && error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      const message = getApiErrorMessage(error as Error, DEFAULT_FETCH_ERROR_MESSAGE);
      const key = `${status ?? 'unknown'}:${message}`;

      if (lastToastKeyRef.current === key) return;
      lastToastKeyRef.current = key;

      toast.error(message);
    }
  }, [isError, error, toast]);

  return query;
};

/**
 * Hook to get a single treatment type by ID
 */
export const useMainFreeMedicalTreatmentType = (id: number) => {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const query = useQuery({
    queryKey: ['free-medical', 'treatment-types', tenant, clinicNumber, id],
    queryFn: () => getTreatmentTypeById(tenant, clinicNumber, id),
    enabled: !!tenant && !!clinicNumber && !!id,
  });

  const { isError, error } = query;
  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error as Error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return query;
};

/**
 * Hook to create a treatment type
 */
export const useMainFreeMedicalTreatmentTypeCreate = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: TreatmentTypeFormData) => createTreatmentType(tenant, clinicNumber, data),
    onSuccess: response => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['free-medical', 'treatment-types'] });
        // creation should display registration success
        toast.success(response.message ?? '自由診療を登録しました。');
      } else {
        toast.error(response.message ?? '登録に失敗しました。');
      }
    },
    onError: error => {
      toast.error(getApiErrorMessage(error as Error, '登録に失敗しました。'));
    },
  });

  return { mutateAsync, isLoading: isPending };
};

/**
 * Hook to update a treatment type
 */
export const useMainFreeMedicalTreatmentTypeUpdate = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<TreatmentTypeFormData> }) =>
      updateTreatmentType(tenant, clinicNumber, id, data),
    onSuccess: response => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['free-medical', 'treatment-types'] });
        queryClient.invalidateQueries({
          queryKey: ['free-medical', 'treatment-types', response.data?.id],
        });
        toast.success(response.message ?? '自由診療を更新しました。');
      } else {
        toast.error(response.message ?? '更新に失敗しました。');
      }
    },
    onError: error => {
      toast.error(getApiErrorMessage(error as Error, '更新に失敗しました。'));
    },
  });

  return { mutateAsync, isLoading: isPending };
};

/**
 * Hook to delete a treatment type
 */
export const useMainFreeMedicalTreatmentTypeDelete = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: number) => deleteTreatmentType(tenant, clinicNumber, id),
    onSuccess: response => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['free-medical', 'treatment-types'] });
        // design specifies a more concise success message without "メニュー"
        toast.success(response.message ?? '自由診療を削除しました。');
      } else {
        toast.error(response.message ?? '削除に失敗しました。');
      }
    },
    onError: error => {
      toast.error(getApiErrorMessage(error as Error, '削除に失敗しました。'));
      queryClient.invalidateQueries({ queryKey: ['free-medical', 'treatment-types'] });
    },
  });

  return { mutateAsync, isLoading: isPending };
};
